import { useCallback, useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import Dialog from "./index";
import Button from "../Button";
import { useVisible } from "../../../utils/hooks/useVisible";
import { useAccess } from "../../../utils/hooks/access";
import { getSonnerErrorMessage } from "../../../utils/helpers/errors";

type UseDeleteDialogOptions<T> = {
    title?: ReactNode;
    message: (item?: T) => ReactNode;
    permission?: string;
    loading?: boolean;
    onConfirm: (item: T) => void;
    onCancel?: () => void;
};

type UseDeleteDialogResult<T> = {
    open: (item: T) => void;
    dialog: ReactNode;
};

export const useDeleteDialog = <T,>({
    title,
    message,
    permission,
    loading,
    onConfirm,
    onCancel,
}: UseDeleteDialogOptions<T>): UseDeleteDialogResult<T> => {
    const { t } = useTranslation();
    const { hasAccess } = useAccess(permission ?? "");
    const { visible, selected: item, open, close } = useVisible();

    const openDialog = useCallback(
        (value: T) => {
            if (permission && !hasAccess) return;
            open(value);
        },
        [permission, hasAccess, open],
    );

    const onClose = useCallback(() => {
        close();
        onCancel?.();
    }, [onCancel, close]);

    const confirm = useCallback(async () => {
        if (item !== undefined) {
            try {
                await onConfirm(item);
                toast.success(
                    Array.isArray(item)
                        ? t("resources-deleted-successfully")
                        : t("deleted-successfully"),
                );
            } catch (error) {
                getSonnerErrorMessage(error, t);
                return;
            }
        }
        close();
    }, [item, onConfirm, close, t]);

    const dialog = useMemo(
        () => (
            <Dialog
                title={title}
                open={visible}
                close={onClose}
                size="lg"
                footer={
                    <>
                        <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={loading}>
                            {t("cancel")}
                        </Button>
                        <Button type="button" variant="destructive" size="sm" onClick={confirm} loading={loading}>
                            {t("confirm")}
                        </Button>
                    </>
                }
            >
                <div className="tw-flex tw-flex-col tw-gap-3">
                    <div className="tw-flex tw-size-10 tw-items-center tw-justify-center tw-rounded-full tw-bg-destructive/10">
                        <AlertTriangle className="tw-size-5 tw-text-destructive" />
                    </div>
                    <div className="tw-space-y-1">
                        <p className="tw-text-sm tw-text-muted-foreground">{message(item)}</p>
                    </div>
                </div>
            </Dialog>
        ),
        [title, t, visible, onClose, loading, confirm, message, item],
    );

    return { open: openDialog, dialog };
};

